import React, {
  useRef,
  useLayoutEffect,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import { getBreadcrumbs } from '../../../util/breadcrumb';
import StructuredData from '../../StructuredData/StructuredData';
import { breadcrumbStructuredData } from '../../../util/structuredData';

interface BreadcrumbContainerProps {
  className?: string;
  isMobile?: boolean;
}

const BreadcrumbContainer: React.FC<BreadcrumbContainerProps> = ({
  className,
  isMobile = false,
}) => {
  const location = useLocation();
  const breadcrumbs = useMemo(() => {
    return getBreadcrumbs(location?.pathname);
  }, [location]);

  const breadcrumbRef = useRef<HTMLDivElement>(null);
  const breadcrumbRef2 = useRef<HTMLDivElement>(null);
  const [windowWidth, setWindowWidth] = useState(0);
  const [allowTruncated, setAllowTruncated] = useState(false);

  // Listen for window resize and update windowWidth
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth);
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  useLayoutEffect(() => {
    if (breadcrumbRef.current) {
      const width = breadcrumbRef.current.offsetWidth;

      if (breadcrumbRef2.current) {
        const width2 = breadcrumbRef2.current.offsetWidth;
        // Only evaluate if both widths are non-zero.
        if (width > 0 && width2 > 0) {
          setAllowTruncated(width > width2);
        }
      }
    }
  }, [breadcrumbs]);

  useLayoutEffect(() => {
    // Check both refs are attached and elements are visible
    if (
      breadcrumbRef.current &&
      breadcrumbRef2.current &&
      breadcrumbRef.current.offsetParent !== null &&
      breadcrumbRef2.current.offsetParent !== null
    ) {
      const width1 = breadcrumbRef.current.offsetWidth;
      const width2 = breadcrumbRef2.current.offsetWidth;

      // Only evaluate if both widths are non-zero.
      if (width1 > 0 && width2 > 0) {
        setAllowTruncated(width1 > width2);
      }
    }
  }, [windowWidth]);

  return (
    <>
      {/* This is not visible and used to determine overflow */}
      <Breadcrumb
        className='not_visible'
        breadcrumbs={breadcrumbs}
        style={{
          position: 'absolute',
          top: '0px',
          visibility: 'hidden',
        }}
        ref={breadcrumbRef}
      />
      {/* Structured data for SEO */}
      <StructuredData
        structuredData={JSON.stringify(breadcrumbStructuredData(breadcrumbs))}
      />
      {/* The displayed breadcrumb */}
      <div ref={breadcrumbRef2}>
        <Breadcrumb
          className={className}
          breadcrumbs={breadcrumbs}
          useOverflowMenu={isMobile && allowTruncated}
        />
      </div>
    </>
  );
};

export default BreadcrumbContainer;
